import { useCallback, useEffect } from 'react';

import { V2 } from '~/src/geometry/vector';
import { p, P2, P3 } from '~/src/geometry/point';
import { UnitViewProps } from '~/src/view/types';
import { get1 } from '~/src/base/Optic';
import { NonEmpty } from '~/src/base/Array/NonEmpty';

import { Figure , _Pieces } from '~/src/figure';
import { Piece } from '~/src/figure/Piece';
import { FigureSize, FragFig, ItemHandlers, ItemType, ViewFigureWrap } from '~/src/figure/View';
import { applyFigureMod } from '~/src/figure/FigureMod';
import { IId } from '~/src/figure/Item';
import useSize from '~/src/hooks/useSize';

import State, { Config , getTweaks } from './Figure/State';
import * as St from './Figure/State';
import * as Act from './Figure/Action';
import { Emitter } from './Figure/Hook';

export interface TweakerProps extends UnitViewProps<State>, FigureSize {
	emit : Emitter;
}

export const _doPan = ( emit : Emitter ) => ( dxy : V2 ) : void =>
	emit( Act.pan( dxy ) );

export const _doZoom = ( emit : Emitter ) => ( dzf : number , pnt : V2 ) : void =>
	emit( Act.zoom( dzf , p( pnt ) ) );

export const _doRotate = ( emit : Emitter ) => ( ang : number , pnt : P2 ) : void =>
	emit( Act.rotate( ang , pnt ) );

export const _doCenter = ( emit : Emitter ) => () : void =>
	emit( Act.center );

export const _doItemMove = ( emit : Emitter ) => ( typ : ItemType , iid : IId , pnt : P2 ) => {
	switch( typ ) {
	case 'Ruler' :
		emit( Act.moveRuler( iid , pnt ) );
		return;
	case 'RulerLabel' :
		emit( Act.moveRulerLabel( iid , pnt ) );
		return;
	}
};

export const filterPiece = ( active : string ) : ( figure : Figure ) => Figure => _Pieces( ps => {
	const index : number = parseInt( active );
	const piece : undefined | Piece = ps[ index ];
	return !piece ? [ ps[0] ] : [ piece ] as NonEmpty< Piece >;
} );

export const Tweaker = ( { width , height , value , emit , ...ps } : TweakerProps ) => {
	const [ ref , size ] = useSize< SVGSVGElement >();

	const pan : V2 = get1( St.pan )( value );
	const zoom : number = get1( St.zoom )( value );
	const ctrd : boolean = get1( St.ctrd )( value );
	const angle : number = get1( St.angle )( value );
	const config : Config = get1( St.config )( value );

	const figure : Figure = filterPiece( value.active )( applyFigureMod( getTweaks( value ) )( value.figure ) );

	const project = useCallback( ( p : P3 ) => St._project( angle )( p ) , [ angle ] );

	const doPan = useCallback( ( dxy : V2 ) => _doPan( emit )( dxy ) , [ emit ] );
	const doZoom = useCallback( ( dzf : number , pnt : V2 ) => _doZoom( emit )( dzf , pnt ) , [ emit ] );
	const doRotate = useCallback( ( ang : number , pnt : P2 ) => _doRotate( emit )( ang , pnt ) , [ emit ] );
	const doCenter = useCallback( () => _doCenter( emit )() , [ emit ] );
	const doItemMove = useCallback( ( typ : ItemType , iid : IId , pnt : P2 ) => { if ( config.editable ) _doItemMove( emit )( typ , iid , pnt ); } , [ emit , config.editable ] );

	useEffect( () => emit( Act.setSize( size ) ) , [ size , emit , value.figure ] );

	useEffect( () => {
		if ( !ctrd || size.w <= 0 || size.h <= 0 ) return;
		emit( Act.centerAll );
	} , [ emit , size , ctrd , value.figure ] );

	return (
		<ViewFigureWrap
			pref="Figure"

			ref={ ref }

			width={ width }
			height={ height }

			pan={ pan }
			zoom={ zoom }

			onPan={ doPan }
			onZoom={ doZoom }
			onCenter={ doCenter }
			onRotate={ doRotate }

			onItemMove={ doItemMove }
			actionables={ [ 'Ruler' , 'RulerLabel' ] }
		>
			{ ( hs : ItemHandlers ) => <FragFig proj={ project } zoom={ zoom } value={ figure } { ...hs } { ...ps } /> }
		</ViewFigureWrap>
	);
};
